import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

const PEEK = "/giraffe-popout.png";
const DRAWER = "/cassetto-giraffe.png";
const CONFETTI = ["#9F583F", "#E0A82E", "#6F7F35", "#C64B2C", "#D9793A", "#EAD3A6"];

// A hidden little giraffe inside a chosen article. Find it -> click to put it
// back in the drawer -> the giraffe thanks the reader. One completion per browser.
export const LostGiraffe = ({ slug }) => {
  const [cfg, setCfg] = useState(null);
  const [stage, setStage] = useState("peek"); // peek | returning | thanks
  const doneKey = slug ? `lost_giraffe_done_${slug}` : null;

  useEffect(() => {
    api.get("/lost-giraffe").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const alreadyDone = doneKey ? !!localStorage.getItem(doneKey) : true;
  const shouldShow = cfg && cfg.active && cfg.slug === slug && !alreadyDone;

  const confetti = useMemo(
    () =>
      Array.from({ length: 40 }).map((_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 520,
        y: -(Math.random() * 340 + 120),
        rot: Math.random() * 360,
        color: CONFETTI[i % CONFETTI.length],
        delay: Math.random() * 0.2,
      })),
    []
  );

  if (!shouldShow && stage === "peek") return null;

  const catchIt = () => {
    setStage("returning");
    setTimeout(() => setStage("thanks"), 1300);
    if (doneKey) localStorage.setItem(doneKey, "1");
  };

  return (
    <>
      {/* Hidden peeking giraffe inside the article */}
      {stage === "peek" && (
        <motion.button
          type="button"
          onClick={catchIt}
          data-testid="lost-giraffe"
          aria-label="Una giraffina perduta! Rimettila nel cassetto"
          className="absolute z-20 cursor-pointer"
          style={{ right: -4, top: "48%", width: 46 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.92, y: [0, -5, 0] }}
          transition={{ opacity: { duration: 0.6 }, y: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }}
          whileHover={{ scale: 1.12, opacity: 1 }}
        >
          <img src={PEEK} alt="" className="w-full h-auto select-none pointer-events-none drop-shadow-md" />
        </motion.button>
      )}

      {/* Overlay: returning to the drawer + thank-you */}
      <AnimatePresence>
        {(stage === "returning" || stage === "thanks") && (
          <motion.div
            className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lost-giraffe-overlay"
            style={{ background: "rgba(28,25,23,0.55)", backdropFilter: "blur(3px)" }}
          >
            <div className="relative w-full max-w-md text-center">
              {/* the giraffe flies down into the drawer */}
              {stage === "returning" && (
                <motion.img
                  src={PEEK}
                  alt=""
                  className="absolute left-1/2 w-16 h-auto drop-shadow-xl"
                  initial={{ top: -160, x: "-50%", rotate: -12, opacity: 1 }}
                  animate={{ top: 120, x: "-50%", rotate: 8, opacity: 0.2, scale: 0.6 }}
                  transition={{ duration: 1.2, ease: "easeIn" }}
                />
              )}

              {stage === "thanks" && (
                <motion.div
                  initial={{ scale: 0.85, y: 30, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 18 }}
                  className="relative bg-white rounded-3xl border-2 border-[#EADFC9] px-7 py-9 shadow-2xl overflow-hidden"
                  data-testid="lost-giraffe-thanks"
                >
                  {/* confetti */}
                  <div className="pointer-events-none absolute inset-0 flex items-start justify-center">
                    {confetti.map((c) => (
                      <motion.span
                        key={c.id}
                        className="absolute top-1/3 w-2 h-3 rounded-sm"
                        style={{ background: c.color }}
                        initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
                        animate={{ x: c.x, y: c.y, opacity: 0, rotate: c.rot }}
                        transition={{ duration: 1.4, delay: c.delay, ease: "easeOut" }}
                      />
                    ))}
                  </div>

                  <img src={DRAWER} alt="Giraffa nel cassetto" className="w-40 h-auto mx-auto mb-4 drop-shadow-lg" />
                  <p className="label-ui text-[#9F583F] mb-2">La Giraffa Perduta</p>
                  <h3 className="font-display text-3xl text-[#1C1917] leading-tight mb-3">
                    L'hai rimessa nel cassetto!
                  </h3>
                  <p className="font-body text-lg text-[#57534E] leading-relaxed">
                    {cfg?.message?.trim() ||
                      "Grazie di cuore! Mi avevi smarrita tra le righe di questo articolo e tu mi hai riportata a casa. 🦒💛"}
                  </p>
                  <button
                    onClick={() => setStage("thanks-closed")}
                    data-testid="lost-giraffe-close"
                    className="mt-7 inline-flex items-center bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-7 py-3 rounded-full transition-colors"
                  >
                    Con piacere 🦒
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
